import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import {
  Sparkles, Mail, MessageCircle, Clock, Calendar, Cake, Send,
  Bell, Users, AlertCircle, Loader2, Eye, Save, Check, ArrowRight, RefreshCw,
} from "lucide-react";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function Toggle({ checked, onChange, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      data-testid={testid}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        checked ? "bg-emerald-500" : "bg-slate-300"
      }`}
    >
      <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
        checked ? "translate-x-6" : "translate-x-1"
      }`} />
    </button>
  );
}

function SectionCard({ icon: Icon, title, count, children, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
          <Icon size={16} />
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Section</div>
          <div className="font-display font-black text-sm">{title}</div>
        </div>
        {count != null && (
          <span className="text-xs font-bold px-2 py-1 rounded-full bg-emerald-50 text-emerald-700">{count}</span>
        )}
      </div>
      {children}
    </div>
  );
}

export default function BriefingPage() {
  const [settings, setSettings] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewKind, setPreviewKind] = useState("daily");
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [saveOk, setSaveOk] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    setLoadingSettings(true);
    try {
      const { data } = await api.get("/briefing/settings");
      setSettings(data);
      setDirty(false);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setLoadingSettings(false);
    }
  };

  const loadPreview = async (kind = previewKind) => {
    setLoadingPreview(true);
    try {
      const { data } = await api.get(`/briefing/preview?kind=${kind}`);
      setPreview(data);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setLoadingPreview(false);
    }
  };

  useEffect(() => { load(); loadPreview("daily"); }, []); // run once on mount

  const update = (patch) => { setSettings((s) => ({ ...s, ...patch })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    setSaveOk(false);
    setError("");
    try {
      const { data } = await api.put("/briefing/settings", settings);
      setSettings(data);
      setDirty(false);
      setSaveOk(true);
      setTimeout(() => setSaveOk(false), 2000);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    setSendResult(null);
    setError("");
    try {
      const { data } = await api.post("/briefing/send-now", { kind: previewKind });
      setSendResult(data);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSending(false);
    }
  };

  // Build a per-channel human-readable status line
  const FRIENDLY_ERRORS = {
    smtp_timeout: "email server timed out",
    smtp_not_connected: "email account not connected",
    smtp_send_failed: "email send failed",
    no_recipient: "no email address on file",
    twilio_not_configured: "WhatsApp is not configured",
    twilio_exception: "WhatsApp send crashed",
    no_whatsapp_number_configured: "no WhatsApp number set",
    twilio_400: "WhatsApp number invalid",
    twilio_401: "WhatsApp credentials invalid",
    twilio_403: "WhatsApp not approved for this number",
    twilio_429: "WhatsApp rate-limited by Twilio",
  };
  const channelStatusLine = (result) => {
    if (!result?.channels) return "";
    const parts = [];
    for (const [ch, res] of Object.entries(result.channels)) {
      if (res?.ok) {
        parts.push(`${ch}: sent → ${res.to || ""}`);
      } else {
        const err = FRIENDLY_ERRORS[res?.error] || res?.error || "failed";
        parts.push(`${ch}: ${err}`);
      }
    }
    return parts.join(" · ");
  };

  if (loadingSettings) {
    return (
      <div className="p-8 flex items-center gap-3 text-slate-500" data-testid="briefing-loading">
        <Loader2 className="animate-spin" size={16} /> Loading briefing settings…
      </div>
    );
  }
  if (!settings) return null;

  const channels = settings.channel === "both" ? ["email", "whatsapp"] : [settings.channel];

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="briefing-page">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-emerald-700">Playmaker AI</div>
          <h1 className="font-display text-3xl font-black tracking-tight mt-1">Morning briefing</h1>
          <p className="text-slate-500 mt-1 max-w-xl text-sm">
            A daily and weekly digest of your agenda, inbox, birthdays, transfer-window action, and
            clients who need a check-in — powered by Claude Sonnet 4.5, delivered to your inbox and WhatsApp.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 text-sm bg-white border border-slate-200 rounded-xl px-3 py-2">
            <span className="text-slate-500">Enabled</span>
            <Toggle
              checked={!!settings.enabled}
              onChange={(v) => update({ enabled: v })}
              testid="briefing-enabled-toggle"
            />
          </div>
          <button
            onClick={save}
            disabled={saving}
            className={`h-10 px-4 rounded-xl text-sm font-bold flex items-center gap-2 disabled:opacity-60 ${
              dirty ? "pm-btn-primary ring-2 ring-emerald-300 ring-offset-2" : "pm-btn-primary"
            }`}
            data-testid="briefing-save-button"
          >
            {saving ? <Loader2 className="animate-spin" size={14} /> : (saveOk ? <Check size={14} /> : <Save size={14} />)}
            {saveOk ? "Saved" : (dirty ? "Save changes" : "Save settings")}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 flex items-center gap-2" data-testid="briefing-error">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT: Settings */}
        <div className="lg:col-span-1 space-y-5">
          <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="briefing-settings-schedule">
            <div className="flex items-center gap-2 mb-4">
              <Clock size={16} className="text-emerald-700" />
              <h2 className="font-display font-black text-sm">When to deliver</h2>
            </div>

            <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Frequency</label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {["daily", "weekly", "both"].map((f) => (
                <button
                  key={f}
                  onClick={() => update({ frequency: f })}
                  className={`h-9 rounded-lg text-xs font-bold border transition-colors ${
                    settings.frequency === f
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`briefing-frequency-${f}`}
                >
                  {f}
                </button>
              ))}
            </div>

            {(settings.frequency === "daily" || settings.frequency === "both") && (
              <div className="mt-4">
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Daily time</label>
                <input
                  type="time"
                  className="pm-input mt-2"
                  value={settings.daily_time || "07:00"}
                  onChange={(e) => update({ daily_time: e.target.value })}
                  data-testid="briefing-daily-time"
                />
              </div>
            )}

            {(settings.frequency === "weekly" || settings.frequency === "both") && (
              <>
                <div className="mt-4">
                  <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Weekly day</label>
                  <div className="grid grid-cols-7 gap-1 mt-2">
                    {WEEKDAYS.map((d, i) => (
                      <button
                        key={d}
                        onClick={() => update({ weekly_day: i })}
                        className={`h-8 rounded-md text-xs font-bold border ${
                          settings.weekly_day === i
                            ? "bg-emerald-500 text-white border-emerald-500"
                            : "bg-white text-slate-700 border-slate-200"
                        }`}
                        data-testid={`briefing-weekly-day-${i}`}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mt-3">
                  <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Weekly time</label>
                  <input
                    type="time"
                    className="pm-input mt-2"
                    value={settings.weekly_time || "07:00"}
                    onChange={(e) => update({ weekly_time: e.target.value })}
                    data-testid="briefing-weekly-time"
                  />
                </div>
              </>
            )}

            <div className="mt-4">
              <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Timezone</label>
              <select
                className="pm-input mt-2"
                value={settings.timezone || "Europe/Amsterdam"}
                onChange={(e) => update({ timezone: e.target.value })}
                data-testid="briefing-timezone"
              >
                {["Europe/Amsterdam", "Europe/London", "Europe/Madrid", "Europe/Berlin", "Europe/Paris", "Europe/Lisbon", "Europe/Rome", "UTC"].map((tz) => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="briefing-settings-channel">
            <div className="flex items-center gap-2 mb-4">
              <Bell size={16} className="text-emerald-700" />
              <h2 className="font-display font-black text-sm">Where to deliver</h2>
            </div>

            <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Channel</label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {[
                { v: "email", label: "Email", icon: Mail },
                { v: "whatsapp", label: "WhatsApp", icon: MessageCircle },
                { v: "both", label: "Both", icon: Sparkles },
              ].map((c) => (
                <button
                  key={c.v}
                  onClick={() => update({ channel: c.v })}
                  className={`h-9 rounded-lg text-xs font-bold border flex items-center justify-center gap-1.5 transition-colors ${
                    settings.channel === c.v
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`briefing-channel-${c.v}`}
                >
                  <c.icon size={12} /> {c.label}
                </button>
              ))}
            </div>

            {channels.includes("email") && (
              <div className="mt-4">
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Email to</label>
                <input
                  type="email"
                  className="pm-input mt-2"
                  placeholder="you@club.com"
                  value={settings.email_to || ""}
                  onChange={(e) => update({ email_to: e.target.value })}
                  data-testid="briefing-email-to"
                />
                <div className="text-[11px] text-slate-500 mt-1">
                  Sent via your connected IMAP/SMTP account.
                </div>
              </div>
            )}

            {channels.includes("whatsapp") && (
              <div className="mt-3">
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">WhatsApp number</label>
                <input
                  type="tel"
                  className="pm-input mt-2"
                  placeholder="+31 6 12345678"
                  value={settings.whatsapp_to || ""}
                  onChange={(e) => update({ whatsapp_to: e.target.value })}
                  data-testid="briefing-whatsapp-to"
                />
                <div className="text-[11px] text-slate-500 mt-1">
                  Uses your workspace Twilio WhatsApp number.
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="briefing-settings-thresholds">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles size={16} className="text-emerald-700" />
              <h2 className="font-display font-black text-sm">Signals & thresholds</h2>
            </div>

            <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Warn N weeks before window opens/closes</label>
            <input
              type="number"
              min="1"
              max="12"
              className="pm-input mt-2"
              value={settings.window_warn_weeks || 4}
              onChange={(e) => update({ window_warn_weeks: parseInt(e.target.value || "0", 10) })}
              data-testid="briefing-window-warn-weeks"
            />

            <label className="text-xs uppercase tracking-widest text-slate-500 font-bold mt-4 block">
              Flag clients silent for N days
            </label>
            <input
              type="number"
              min="1"
              max="120"
              className="pm-input mt-2"
              value={settings.stale_client_days || 14}
              onChange={(e) => update({ stale_client_days: parseInt(e.target.value || "0", 10) })}
              data-testid="briefing-stale-client-days"
            />

            <label className="text-xs uppercase tracking-widest text-slate-500 font-bold mt-4 block">
              Flag contacts silent for N days
            </label>
            <input
              type="number"
              min="1"
              max="180"
              className="pm-input mt-2"
              value={settings.stale_contact_days || 30}
              onChange={(e) => update({ stale_contact_days: parseInt(e.target.value || "0", 10) })}
              data-testid="briefing-stale-contact-days"
            />

            <div className="mt-4 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Include newsletters</div>
                <div className="text-[11px] text-slate-500 mt-0.5">Off by default</div>
              </div>
              <Toggle
                checked={!!settings.include_newsletters}
                onChange={(v) => update({ include_newsletters: v })}
                testid="briefing-include-newsletters"
              />
            </div>
          </div>
        </div>

        {/* RIGHT: Preview */}
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="briefing-preview-header">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Eye size={16} className="text-emerald-700" />
                <h2 className="font-display font-black text-sm">Live preview</h2>
              </div>
              <div className="flex items-center gap-2">
                <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden text-xs font-bold">
                  {["daily", "weekly"].map((k) => (
                    <button
                      key={k}
                      onClick={() => { setPreviewKind(k); loadPreview(k); }}
                      className={`px-3 h-8 ${previewKind === k ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`}
                      data-testid={`briefing-preview-tab-${k}`}
                    >
                      {k}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => loadPreview(previewKind)}
                  disabled={loadingPreview}
                  className="pm-btn-secondary h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5"
                  data-testid="briefing-preview-refresh"
                >
                  {loadingPreview ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />} Refresh
                </button>
                <button
                  onClick={sendNow}
                  disabled={sending || !settings.enabled}
                  className="pm-btn-primary h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5 disabled:opacity-60"
                  data-testid="briefing-send-now"
                >
                  {sending ? <Loader2 className="animate-spin" size={12} /> : <Send size={12} />} Send me now
                </button>
              </div>
            </div>
            {sendResult && (
              <div
                className={`mt-3 text-xs rounded-lg px-3 py-2 border ${
                  Object.values(sendResult.channels || {}).every((r) => r?.ok)
                    ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                    : "bg-amber-50 border-amber-200 text-amber-900"
                }`}
                data-testid="briefing-send-result"
              >
                <div className="font-bold">{channelStatusLine(sendResult)}</div>
                <div className="mt-1 opacity-80">
                  agenda {sendResult.brief_summary?.agenda} · inbox {sendResult.brief_summary?.inbox_kept} ·
                  birthdays {sendResult.brief_summary?.birthdays} ·
                  windows {sendResult.brief_summary?.windows} ·
                  retention {sendResult.brief_summary?.retention}
                </div>
              </div>
            )}
          </div>

          {loadingPreview && !preview && (
            <div className="p-8 text-center text-slate-500 flex items-center justify-center gap-2">
              <Loader2 className="animate-spin" size={16} /> Generating briefing…
            </div>
          )}

          {preview && (
            <>
              <SectionCard icon={Calendar} title="Agenda highlights" count={preview.sections.agenda.count} testid="briefing-preview-agenda">
                {preview.sections.agenda.items.length ? (
                  <ul className="divide-y divide-slate-100">
                    {preview.sections.agenda.items.map((i) => (
                      <li key={i.id} className="py-2 flex items-center gap-2 text-sm">
                        <span className="text-slate-500 font-mono text-xs w-24">{i.when_label}</span>
                        <span className="font-medium text-slate-900">{i.title}</span>
                        {i.client_name && <span className="text-slate-500">· {i.client_name}</span>}
                        {i.conflict && <span className="ml-auto text-amber-600 text-xs font-bold">⚠ conflict</span>}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-sm text-slate-500">Nothing scheduled for this {previewKind === "daily" ? "day" : "week"}.</div>
                )}
              </SectionCard>

              <SectionCard
                icon={Mail}
                title={`Inbox — ${preview.sections.inbox.kept} relevant / ${preview.sections.inbox.total_scanned} scanned`}
                testid="briefing-preview-inbox"
              >
                {preview.sections.inbox.items.length ? (
                  <ul className="divide-y divide-slate-100">
                    {preview.sections.inbox.items.map((i, idx) => (
                      <li key={idx} className="py-2 text-sm flex items-start gap-2">
                        {i.urgent && <span className="text-red-500 font-bold">●</span>}
                        <div className="flex-1">
                          <div className="font-medium text-slate-900">
                            {i.sender} <span className="text-slate-400 text-xs">· {i.channel}</span>
                          </div>
                          <div className="text-slate-600 text-[13px]">{i.one_liner}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-sm text-slate-500">Claude found nothing worth surfacing.</div>
                )}
              </SectionCard>

              <SectionCard icon={Cake} title="Birthday reminders" count={preview.sections.birthdays.count} testid="briefing-preview-birthdays">
                {preview.sections.birthdays.items.length ? (
                  <ul className="divide-y divide-slate-100">
                    {preview.sections.birthdays.items.map((i) => (
                      <li key={i.id} className="py-2 text-sm flex items-center gap-3">
                        <span className="text-slate-500 w-24">{i.when_label}</span>
                        <span className="font-medium text-slate-900">{i.who}</span>
                        <span className="text-slate-500">{i.role}</span>
                        {i.age && <span className="ml-auto text-emerald-700 text-xs font-bold">turns {i.age}</span>}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-sm text-slate-500">No birthdays coming up.</div>
                )}
              </SectionCard>

              <SectionCard icon={ArrowRight} title="Transfer window watch" count={preview.sections.windows.items.length} testid="briefing-preview-windows">
                {preview.sections.windows.items.length ? (
                  <ul className="space-y-3">
                    {preview.sections.windows.items.map((w, idx) => (
                      <li key={idx} className="text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">{w.league}</span>
                          <span className="text-slate-500">· {w.country} · {w.window_type}</span>
                          <span className={`ml-auto text-xs font-bold ${w.state === "open_now" ? "text-red-600" : "text-amber-600"}`}>
                            {w.label}
                          </span>
                        </div>
                        {w.expiring_clients?.length > 0 && (
                          <div className="mt-1 text-[12px] text-slate-600">
                            Expiring: {w.expiring_clients.map((e) => `${e.client_name} (${e.expiry.slice(0, 7)})`).join(", ")}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-sm text-slate-500">No windows in the horizon.</div>
                )}
              </SectionCard>

              <SectionCard
                icon={Users}
                title={`Client retention (>${preview.sections.retention.threshold_days}d silent)`}
                count={preview.sections.retention.items.length}
                testid="briefing-preview-retention"
              >
                {preview.sections.retention.items.length ? (
                  <ul className="divide-y divide-slate-100">
                    {preview.sections.retention.items.slice(0, 10).map((i) => (
                      <li key={i.client_id} className="py-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">{i.client_name}</span>
                          {i.position && <span className="text-slate-500 text-xs">· {i.position}</span>}
                          {i.current_club && <span className="text-slate-500 text-xs">· {i.current_club}</span>}
                          <span className="ml-auto text-slate-500 text-xs">
                            {i.days_since != null ? `${i.days_since}d ago` : "no record"}
                          </span>
                        </div>
                        <div className="text-slate-600 text-[12px]">{i.suggestion}</div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-sm text-slate-500">Everyone reached recently.</div>
                )}
              </SectionCard>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
